import React, { useEffect, useState } from 'react';
import { auth, db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  allowedRole?: string | string[];
}

export default function ProtectedRoute({ children, allowedRoles, allowedRole }: ProtectedRouteProps) {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  // Normalize roles list
  const rolesList = React.useMemo(() => {
    if (allowedRoles && Array.isArray(allowedRoles)) return allowedRoles;
    if (allowedRole) {
      if (Array.isArray(allowedRole)) return allowedRole;
      return [allowedRole];
    }
    return ['admin', 'editor', 'seller', 'user'];
  }, [allowedRoles, allowedRole]);

  useEffect(() => {
    const checkAuth = async (user: any) => {
      if (!user) {
        setAuthorized(false);
        setLoading(false);
        return;
      }

      try {
        let role = 'user';
        
        // 1. Check users collection
        const userDocRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(userDocRef).catch(e => { console.error('Error getting user doc:', e); throw e; });
        
        if (docSnap.exists() && docSnap.data().role) {
          role = docSnap.data().role;
        } else {
          // 2. Fallback check user_roles collection
          const userEmail = user.email || '';
          if (!userEmail) throw new Error('No email');
          const roleDocRef = doc(db, 'user_roles', userEmail.toLowerCase());
          const roleSnap = await getDoc(roleDocRef).catch(e => { console.error('Error getting role doc:', e); throw e; });
          if (roleSnap.exists() && roleSnap.data().role) {
            role = roleSnap.data().role;
          }
        }
        
        // 3. Fallback for system owner
        const userEmail = user.email || '';
        const isSystemOwner = userEmail.toLowerCase() === 'priyofy@gmail.com';
        if (isSystemOwner) role = 'admin';

        if (rolesList.includes(role) || rolesList.includes('user')) {
          setAuthorized(true);
        } else {
          setAuthorized(false);
        }
      } catch (err: any) {
        console.error('Error fetching role:', err.message, err.stack);
        setAuthorized(false);
      }
      setLoading(false);
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
       checkAuth(user);
    });
    
    return () => unsubscribe();
  }, [rolesList]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0C]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!authorized) {
    window.location.href = window.location.origin;
    return null;
  }

  return <>{children}</>;
}
